"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateResponseStatus = exports.isObject = exports.tryParseJSON = exports.fromError = exports.fromAppProps = exports.fromIncomingProps = exports.fromAppPropsWithQueryLocale = exports.fromIncomingPropsWithQueryLocale = exports.withQueryParamLocale = exports.fetchProps = void 0;
const web_headers_1 = require("@snapchat/web-headers");
const web_i18n_1 = require("@snapchat/web-i18n");
const web_log_1 = require("@snapchat/web-log");
const exponential_backoff_1 = require("exponential-backoff");
const node_fetch_1 = require("node-fetch");
const endpoints_1 = require("../constants/endpoints");
const statuses_1 = require("./statuses");
const web_http_1 = require("@snapchat/web-http");
// these are the headers we'll forward to web attachments service
const ALLOWED_HEADERS = [
    "accept-language",
    "cloudfront-viewer-country",
    "cloudfront-viewer-time-zone",
    "cookie",
    "referer",
    "x-forwarded-for", // used to determine IP address
];
function toURL(request) {
    // eslint-disable-next-line no-restricted-globals
    const host = request ? request.headers.host : window.location.hostname;
    const protocol = host?.indexOf("localhost") === 0 ? "http" : "https";
    // eslint-disable-next-line no-restricted-globals
    const path = request ? request.url : window.location.pathname + window.location.search;
    return new URL(`${protocol}://${host || ""}${path || ""}`);
}
async function fetchProps(request, propsDecoder, gaid, fallbackOrigin, isEmbed, timeout, backendEndpointUrl) {
    const url = toURL(request);
    // this will be false even when `cloudfront-is-mobile-viewer` header does not exist,
    // which is intentional since we want the default value to be always false for SSR purposes.
    const isMobile = request?.headers["cloudfront-is-mobile-viewer"] !== "false";
    const viewerCountry = await (0, web_headers_1.extractCountryFromHeaders)(request?.headers, web_log_1.log.error);
    const viewerCountrySuffix = viewerCountry.length === 2 ? `${url.search ? "&" : "?"}vc=${viewerCountry}` : "";
    let pathName = url.pathname;
    if (isEmbed) {
        // We fetch '/{id}/embed' page props from '/{id}' backend endpoint.
        pathName = url.pathname.replace(/\/embed$/, "");
    }
    // Strip the locale from pathname if it exists
    pathName = pathName.replace(/(\/[a-z]{2}(-[A-Z]{2})?)\/?$/, "/");
    const backendUrl = backendEndpointUrl || (0, endpoints_1.backendEndpoint)();
    const dataURL = `${backendUrl}${pathName}${url.search}${viewerCountrySuffix}`;
    const headers = (0, web_headers_1.transform)(request?.headers, ALLOWED_HEADERS, fallbackOrigin);
    const response = await fetchWithRetries(2, dataURL, headers, timeout);
    const text = await response.text();
    if (response.status === 200) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const serverPageProps = propsDecoder.fromJSON(JSON.parse(text));
        return { ...serverPageProps, gaid, isMobile };
    }
    else if ([414, 408, 500, 503].includes(response.status)) {
        // don't try to parse props from 414, 408 or common 5xx
        // these requests aren't handled by the b/e app, so we won't have valid JSON props
        const responseHeaders = Array.from(response.headers.entries());
        throw new statuses_1.ErrorWithStatus(`fetchProps got ${response.status} for ${dataURL} headers ${JSON.stringify(responseHeaders)}`, (0, statuses_1.toStatus)(response.status), { text });
    }
    else {
        const responseHeaders = Array.from(response.headers.entries());
        let errorBody = {};
        try {
            errorBody = text ? JSON.parse(text) : {};
        }
        catch (e) {
            web_log_1.log.warn("Failed to parse error response to JSON: ", e);
            web_log_1.log.warn("Response body: ", text);
        }
        throw new statuses_1.ErrorWithStatus(`fetchProps got ${response.status} for ${dataURL} headers ${JSON.stringify(responseHeaders)}`, (0, statuses_1.toStatus)(response.status), errorBody);
    }
}
exports.fetchProps = fetchProps;
function isRetriable(e) {
    // node-fetch throws a FetchError:
    //   https://github.com/node-fetch/node-fetch/blob/ffef5e3c2322e8493dd75120b1123b01b106ab23/src/index.js#L94
    // which has a 'code' field:
    //   https://github.com/node-fetch/node-fetch/blob/ffef5e3c2322e8493dd75120b1123b01b106ab23/src/errors/fetch-error.js#L19
    // which should be 'ETIMEDOUT' on timeouts:
    //   https://stackoverflow.com/questions/23632914/how-to-handle-etimedout-error
    return e instanceof node_fetch_1.FetchError && e.code === "ETIMEDOUT";
}
async function fetchWithRetries(retries, url, headers, 
// by dafault, timeout after 10 seconds
timeout) {
    let fetchPromise;
    if (!timeout) {
        fetchPromise = fetch(url, headers);
    }
    else {
        const unauthedFetch = (0, web_http_1.interceptFetch)({
            name: "FetchProps",
            metricsPrefix: "proto-props",
            timeout,
        });
        fetchPromise = unauthedFetch(url, headers);
    }
    return (0, exponential_backoff_1.backOff)(async () => fetchPromise, {
        jitter: "full",
        numOfAttempts: 1 + retries,
        retry: isRetriable,
    });
}
/**
 * withQueryParamLocale overwrites `viewerInfo.locale` in incominng props if a valid
 * locale is provided via request query parameter (e.g. ?locale=en-US)
 *
 * @param request next.js request object
 * @param incomingProps the incoming page props
 * @param messagesLoader optional async function to load localized messages
 */
async function withQueryParamLocale(request, incomingProps, messagesLoader) {
    if (isObject(incomingProps.viewerInfo) && incomingProps.viewerInfo.locale) {
        const locale = (0, web_i18n_1.getServerLocale)(request);
        const appProps = {
            ...incomingProps,
            viewerInfo: {
                ...incomingProps.viewerInfo,
                ...(locale && {
                    locale,
                }),
            },
        };
        return {
            ...appProps,
            ...(messagesLoader &&
                appProps.viewerInfo.locale && {
                messages: await messagesLoader(appProps.viewerInfo.locale),
            }),
        };
    }
    return incomingProps;
}
exports.withQueryParamLocale = withQueryParamLocale;
/**
 * The server supplied locale uses the underscore format (i.e en_US) - IntlProvider
 * requires ISO-639, so we update the incoming props to adhere to this - if the props
 * do contain the viewerInfo/locale - else it's a NOOP
 *
 * @param incomingProps the incoming page props
 */
function withFormattedLocale(incomingProps) {
    if (isObject(incomingProps.viewerInfo) && incomingProps.viewerInfo.locale) {
        const maybeLocale = incomingProps.viewerInfo.locale;
        return {
            ...incomingProps,
            viewerInfo: {
                ...incomingProps.viewerInfo,
                locale: (0, web_i18n_1.getLocale)(maybeLocale),
            },
        };
    }
    return incomingProps;
}
function tryGetPageProps(appProps) {
    if (isObject(appProps) && isObject(appProps.pageProps)) {
        return withFormattedLocale(appProps.pageProps);
    }
    else {
        return undefined;
    }
}
async function fromIncomingPropsWithQueryLocale(request, incomingProps, messagesLoader) {
    return {
        pageProps: await withQueryParamLocale(request, incomingProps, messagesLoader),
        status: web_log_1.Status.Ok,
    };
}
exports.fromIncomingPropsWithQueryLocale = fromIncomingPropsWithQueryLocale;
async function fromAppPropsWithQueryLocale(request, appProps, messagesLoader) {
    const pageProps = tryGetPageProps(appProps);
    if (pageProps) {
        return fromIncomingPropsWithQueryLocale(request, pageProps, messagesLoader);
    }
    const status = web_log_1.Status.Ok;
    return { ...appProps, pageProps: { status }, status };
}
exports.fromAppPropsWithQueryLocale = fromAppPropsWithQueryLocale;
function fromIncomingProps(incomingProps) {
    return { pageProps: withFormattedLocale(incomingProps), status: web_log_1.Status.Ok };
}
exports.fromIncomingProps = fromIncomingProps;
function fromAppProps(appProps) {
    const pageProps = tryGetPageProps(appProps);
    if (pageProps) {
        return fromIncomingProps(pageProps);
    }
    const status = web_log_1.Status.Ok;
    return { ...appProps, pageProps: { status }, status };
}
exports.fromAppProps = fromAppProps;
function fromError(error) {
    return { pageProps: { ...error.props }, status: error.status };
}
exports.fromError = fromError;
function tryParseJSON(string) {
    try {
        return JSON.parse(string);
    }
    catch (e) {
        return undefined;
    }
}
exports.tryParseJSON = tryParseJSON;
function isObject(value) {
    return typeof value === "object" && value !== null;
}
exports.isObject = isObject;
function updateResponseStatus(res, err) {
    if (res) {
        switch (err.status) {
            case web_log_1.Status.BadRequest:
                res.statusCode = 400;
                break;
            case web_log_1.Status.NotFound:
                res.statusCode = 404;
                break;
            case web_log_1.Status.Unavailable:
                res.statusCode = 451;
                break;
            case web_log_1.Status.Unauthorized:
                res.statusCode = 401;
                break;
            case web_log_1.Status.Gone:
                res.statusCode = 410;
                break;
            case web_log_1.Status.UriTooLong:
                res.statusCode = 414;
                break;
            case web_log_1.Status.InternalServerError:
                res.statusCode = 500;
                break;
            case web_log_1.Status.ServiceUnavailable:
                res.statusCode = 503;
                break;
            case web_log_1.Status.RequestTimeout:
                res.statusCode = 408;
                break;
            case web_log_1.Status.Ok:
                break;
        }
    }
}
exports.updateResponseStatus = updateResponseStatus;
//# sourceMappingURL=pagedata.js.map